/**
 * ESRS E3 — Water & Marine Resources
 *
 * Site-level water accounting and the E3-4 quantitative disclosure. Every
 * volume traces to a site record; basin water-stress uses WRI Aqueduct
 * baseline labels supplied per site (no Aqueduct API integration exists —
 * see backend/services/esrs_e3_service.py).
 *
 * Backend contract (backend/routes/esrs_e3_routes.py):
 *   GET  /api/esrs/e3/disclosure       → { esrs_e3_4_disclosure: {...} }
 *   GET  /api/esrs/e3/water-records     → { records: [...] }
 *   GET  /api/esrs/e3/stressed-sites    → { stressed_sites: [...] }
 *   POST /api/esrs/e3/water-records     → record
 */
import { useCallback, useEffect, useState } from "react";
import {
  Droplets, Waves, Recycle, AlertTriangle, Gauge, Plus, X, MapPin,
} from "lucide-react";
import {
  Page, PageHeader, Card, Overline, Metric, Badge, Data, Btn, DataTable,
  EmptyState, ErrorBanner, Skeleton, field, Label, apiFetch, fmt, C,
} from "../../../components/esg/EsgKit";
import { useEntities } from "../../../hooks/useEntities";

const STRESS_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "low_medium", label: "Low–medium" },
  { value: "medium_high", label: "Medium–high" },
  { value: "high", label: "High" },
  { value: "extremely_high", label: "Extremely high" },
];

// WRI Aqueduct baseline stress label → Badge tone + icon.
function stressBadge(cat) {
  switch (cat) {
    case "extremely_high":
      return <Badge c={C.destructive} Icon={AlertTriangle} label="Extremely high" />;
    case "high":
      return <Badge c={C.destructive} Icon={AlertTriangle} label="High" />;
    case "medium_high":
      return <Badge c={C.accent} Icon={Waves} label="Medium–high" />;
    case "low_medium":
      return <Badge c={C.neutral} Icon={Droplets} label="Low–medium" />;
    case "low":
      return <Badge c={C.neutral} Icon={Droplets} label="Low" />;
    default:
      return <Badge c={C.neutral} Icon={Droplets} label={cat || "—"} />;
  }
}

const num = (v) => <Data color={C.body}>{fmt(v)}</Data>;

const EMPTY_FORM = {
  facility_id: "",
  facility_name: "",
  basin_name: "",
  withdrawal_m3: "",
  discharge_m3: "",
  recycled_reused_m3: "",
  stress_category: "low",
};

export default function E3Water() {
  const { entities, entityId, setEntityId } = useEntities("esrs_e3_entity_id");
  const [period, setPeriod] = useState("FY2026");
  const [periodDraft, setPeriodDraft] = useState("FY2026");

  const [disclosure, setDisclosure] = useState(null);
  const [records, setRecords] = useState([]);
  const [stressed, setStressed] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError("");
    const q = `entity_id=${encodeURIComponent(entityId)}&reporting_period=${encodeURIComponent(period)}`;
    try {
      const [disc, recs, sites] = await Promise.all([
        apiFetch(`/api/esrs/e3/disclosure?${q}`),
        apiFetch(`/api/esrs/e3/water-records?${q}`),
        apiFetch(`/api/esrs/e3/stressed-sites?${q}`),
      ]);
      setDisclosure(disc?.esrs_e3_4_disclosure || null);
      setRecords(recs?.records || []);
      setStressed(sites?.stressed_sites || []);
    } catch (e) {
      setError(e.message || "Failed to load water data");
      setDisclosure(null);
      setRecords([]);
      setStressed([]);
    } finally {
      setLoading(false);
    }
  }, [entityId, period]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!entityId) return;
    setSaving(true);
    setError("");
    try {
      await apiFetch("/api/esrs/e3/water-records", {
        method: "POST",
        body: {
          entity_id: entityId,
          reporting_period: period,
          facility_id: form.facility_id.trim(),
          facility_name: form.facility_name.trim(),
          basin_name: form.basin_name.trim(),
          withdrawal_m3: Number(form.withdrawal_m3 || 0),
          discharge_m3: Number(form.discharge_m3 || 0),
          recycled_reused_m3: Number(form.recycled_reused_m3 || 0),
          stress_category: form.stress_category,
        },
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err.message || "Failed to record water data");
    } finally {
      setSaving(false);
    }
  };

  const periodInput = (
    <input
      value={periodDraft}
      onChange={(e) => setPeriodDraft(e.target.value)}
      onBlur={() => setPeriod(periodDraft)}
      onKeyDown={(e) => { if (e.key === "Enter") setPeriod(periodDraft); }}
      aria-label="Reporting period"
      placeholder="FY2026"
      style={{ ...field, width: 110, textAlign: "center" }}
    />
  );

  const header = (
    <PageHeader
      overline="ESRS E3 · Water & Marine Resources"
      OverlineIcon={Droplets}
      title="Water & Marine Resources"
      subtitle="E3-4 quantitative disclosure — water withdrawal, discharge and consumption per site, with consumption in water-stress areas flagged from WRI Aqueduct baseline stress basins."
      entities={entities}
      entityId={entityId}
      onEntityChange={setEntityId}
      onRefresh={load}
      loading={loading}
      actions={periodInput}
    />
  );

  if (!entityId) {
    return (
      <Page>
        {header}
        <Card>
          <EmptyState
            Icon={Droplets}
            title="No reporting entity selected"
            body="Choose a reporting entity to view its ESRS E3-4 water disclosure, site records and water-stress basin exposure."
          />
        </Card>
      </Page>
    );
  }

  const d = disclosure || {};

  const kpis = [
    { label: "Total withdrawal", value: fmt(d.total_withdrawal_m3), unit: "m³" },
    { label: "Total consumption", value: fmt(d.total_consumption_m3), unit: "m³", color: C.locked },
    { label: "Recycled / reused", value: fmt(d.recycled_reused_m3), unit: "m³", color: C.primary },
    {
      label: "Consumption in water-stress areas",
      value: fmt(d.consumption_in_water_stress_areas_m3),
      unit: "m³",
      color: (d.consumption_in_water_stress_areas_m3 || 0) > 0 ? C.destructive : C.ink,
    },
  ];

  const stressedColumns = [
    {
      key: "facility",
      label: "Facility",
      render: (r) => (
        <Data>{r.facility_name || r.facility_id || "—"}</Data>
      ),
    },
    {
      key: "basin_name",
      label: "Basin",
      render: (r) => <Data color={C.body}>{r.basin_name || "—"}</Data>,
    },
    { key: "withdrawal_m3", label: "Withdrawal (m³)", align: "right", render: (r) => num(r.withdrawal_m3) },
    {
      key: "stress_category",
      label: "Basin stress",
      render: (r) => stressBadge(r.stress_category),
    },
  ];

  const recordColumns = [
    {
      key: "facility",
      label: "Facility",
      render: (r) => <Data>{r.facility_name || r.facility_id || "—"}</Data>,
    },
    {
      key: "basin_name",
      label: "Basin",
      render: (r) => <Data color={C.body}>{r.basin_name || "—"}</Data>,
    },
    { key: "withdrawal_m3", label: "Withdrawal (m³)", align: "right", render: (r) => num(r.withdrawal_m3) },
    { key: "discharge_m3", label: "Discharge (m³)", align: "right", render: (r) => num(r.discharge_m3) },
    {
      key: "consumption_m3",
      label: "Consumption (m³)",
      align: "right",
      render: (r) => <Data color={C.ink}>{fmt(r.consumption_m3)}</Data>,
    },
    { key: "recycled_reused_m3", label: "Recycled (m³)", align: "right", render: (r) => num(r.recycled_reused_m3) },
    {
      key: "stress_category",
      label: "Basin stress",
      render: (r) => stressBadge(r.stress_category),
    },
  ];

  return (
    <Page>
      {header}
      <ErrorBanner message={error} onDismiss={() => setError("")} />

      {loading && !disclosure ? (
        <div style={{ display: "grid", gap: 16 }}>
          <Skeleton h={110} />
          <Skeleton h={200} />
          <Skeleton h={200} />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {/* KPI strip */}
          <Card>
            <Overline Icon={Gauge}>E3-4 disclosure · {period}</Overline>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 20,
              }}
            >
              {kpis.map((k) => (
                <Metric key={k.label} label={k.label} value={k.value} unit={k.unit} color={k.color} />
              ))}
            </div>
            <div
              style={{
                marginTop: 16,
                display: "flex",
                gap: 20,
                flexWrap: "wrap",
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 12,
                color: C.body,
              }}
            >
              <span>
                Sites reporting <Data color={C.ink}>{fmt(d.sites_reporting, 0)}</Data>
              </span>
              <span>
                Sites in high-stress basins{" "}
                <Data color={(d.sites_in_high_stress_basins || 0) > 0 ? C.destructive : C.ink}>
                  {fmt(d.sites_in_high_stress_basins, 0)}
                </Data>
              </span>
              <span>
                Recycled share{" "}
                <Data color={C.primary}>
                  {d.recycled_pct === null || d.recycled_pct === undefined ? "—" : `${fmt(d.recycled_pct, 1)}%`}
                </Data>
              </span>
            </div>
          </Card>

          {/* Sites in water-stressed basins */}
          <Card>
            <Overline Icon={AlertTriangle} color={C.body}>
              Sites in water-stressed basins
            </Overline>
            {stressed.length === 0 ? (
              <EmptyState
                Icon={MapPin}
                title="No sites in high-stress basins"
                body="No reported site falls in a WRI Aqueduct high or extremely-high baseline water-stress basin for this reporting period."
              />
            ) : (
              <DataTable
                columns={stressedColumns}
                rows={stressed}
                keyField="facility_id"
                emptyLabel="No sites in high-stress basins"
              />
            )}
          </Card>

          {/* Record water data */}
          <Card>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <Overline Icon={Plus} color={C.body} style={{ marginBottom: 0 }}>
                Record water data
              </Overline>
              <Btn
                variant={showForm ? "ghost" : "secondary"}
                Icon={showForm ? X : Plus}
                onClick={() => setShowForm((s) => !s)}
              >
                {showForm ? "Cancel" : "Add site record"}
              </Btn>
            </div>

            {showForm && (
              <form onSubmit={submit} style={{ marginTop: 16 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: 14,
                  }}
                >
                  <div>
                    <Label>Facility ID *</Label>
                    <input
                      value={form.facility_id}
                      onChange={(e) => setField("facility_id", e.target.value)}
                      required
                      placeholder="plant-042"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Facility name</Label>
                    <input
                      value={form.facility_name}
                      onChange={(e) => setField("facility_name", e.target.value)}
                      placeholder="Seville bottling plant"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Basin name</Label>
                    <input
                      value={form.basin_name}
                      onChange={(e) => setField("basin_name", e.target.value)}
                      placeholder="Guadalquivir"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Withdrawal (m³) *</Label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={form.withdrawal_m3}
                      onChange={(e) => setField("withdrawal_m3", e.target.value)}
                      required
                      placeholder="0"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Discharge (m³)</Label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={form.discharge_m3}
                      onChange={(e) => setField("discharge_m3", e.target.value)}
                      placeholder="0"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Recycled / reused (m³)</Label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={form.recycled_reused_m3}
                      onChange={(e) => setField("recycled_reused_m3", e.target.value)}
                      placeholder="0"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Basin stress (WRI Aqueduct baseline) *</Label>
                    <select
                      value={form.stress_category}
                      onChange={(e) => setField("stress_category", e.target.value)}
                      required
                      style={field}
                    >
                      {STRESS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                  <Btn type="submit" Icon={Plus} disabled={saving || !form.facility_id.trim() || form.withdrawal_m3 === ""}>
                    {saving ? "Saving…" : "Record"}
                  </Btn>
                  <Btn variant="secondary" onClick={() => { setForm(EMPTY_FORM); setShowForm(false); }}>
                    Reset
                  </Btn>
                </div>
                <p style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: 11, color: C.body, margin: "10px 0 0" }}>
                  Consumption is computed by the platform as withdrawal − discharge (ESRS E3-4 definition).
                </p>
              </form>
            )}
          </Card>

          {/* Water records */}
          <Card>
            <Overline Icon={Recycle} color={C.body}>
              Water records
            </Overline>
            {records.length === 0 ? (
              <EmptyState
                Icon={Droplets}
                title="No water records yet"
                body="Record site-level water withdrawal, discharge and recycled volumes to build the ESRS E3-4 disclosure for this period."
              />
            ) : (
              <DataTable
                columns={recordColumns}
                rows={records}
                keyField="record_id"
                emptyLabel="No water records"
              />
            )}
          </Card>
        </div>
      )}
    </Page>
  );
}
